import { t } from 'i18next';
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { cloneDeep, debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PAGINATION_PAGE_SIZE } from 'basics/constants/common.constant';
import {
  mayaCepTemplateCreateRoute,
  mayaCiTemplateCreateRoute,
  mayaCiTemplateEditRoute,
  mayaCustomerInitiativeDuplicateRoute,
} from 'basics/constants/routes.constants';
import { CiStatus } from 'basics/enums/maya.enums';
import { useSetCiStatusToDeletedMutation } from 'basics/graphql/mutations/setCiStatusToDeleted';
import useGetAllCustomerInitiativeQuery from 'basics/graphql/queries/getAllCustomerInitiatives';
import { SelectOption } from 'basics/options/options.types';
import { succesToast, warningToast } from 'basics/utils/toast';
import { useCurrentUserState } from 'contexts/user';
import { CustomerInitiative, SortFilter } from 'generated/maya.types';
import useCiTemplateListConfig from 'routes/AuthenticatedApp/CiTemplatesList/CiTemplateList.config';

const useCiTemplateList = () => {
  const navigate = useNavigate();
  const flags = useFlags();
  const [ciTitleFilter, setCiTitleFilter] = useState<(string)>();
  const [setCiStatusToDeleted, { loading: removeCiLoading }] = useSetCiStatusToDeletedMutation();
  const [cis, setCis] = useState<CustomerInitiative[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);
  const [rowCountState, setRowCountState] = useState(0);
  const [open, setOpen] = useState(false);
  const [rowId, setRowId] = useState('');
  const [ciTemplateSortFilter, setCiTemplateSortFilter] = useState(SortFilter.NEWEST);
  const { user } = useCurrentUserState();

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const queryFilters = useMemo(
    () => ({
      title: ciTitleFilter,
      countries: user?.country === 'CA' && flags.mayaAkma846EnableDisplaySiForSpecificCountry ? [user?.country] : undefined,
      isTemplate: true,
      status: [CiStatus.ACTIVE, CiStatus.DRAFT],
    }),
    [ciTitleFilter, user?.country, flags.mayaAkma846EnableDisplaySiForSpecificCountry],
  );

  const { data, loading, count } = useGetAllCustomerInitiativeQuery(queryOptions, queryFilters, ciTemplateSortFilter);

  useEffect(() => {
    setRowCountState((prevRowCountState) => (count !== undefined ? count : prevRowCountState));
  }, [count, setRowCountState]);

  useEffect(() => {
    if (!loading) {
      setCis(data);
    }
  }, [data, loading]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const debouncedSearch = useCallback(() => {
    return debounce((searchValue) => setCiTitleFilter(searchValue), 750);
  },
  [setCiTitleFilter]);

  const handleChangeCiTitle = useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    debouncedSearch()(event.target.value);
  }, [debouncedSearch]);

  const editCiTemplateCallback = async (cepId: string) => {
    navigate(`${mayaCiTemplateEditRoute}?id=${cepId}`);
  };

  const duplicateCiTemplateCallback = async (cepId: string) => {
    navigate(`${mayaCustomerInitiativeDuplicateRoute}?cepSourceId=${cepId}`);
  };

  const addChildCallback = async (cepId: string) => {
    navigate(`${mayaCepTemplateCreateRoute}?cepSourceId=${cepId}`);
  };

  const deleteCiCallback = async (ciId: string) => {
    const { data: ciDeleted, error: cidDeletedError } = await setCiStatusToDeleted(ciId, false);
    if (ciDeleted && !cidDeletedError) {
      succesToast(t('maya_action_deleting_ci_success'));
      const clonedCis = cloneDeep(cis);
      const targetCiIndex = clonedCis.findIndex((ci) => { return ci._id === ciId; });
      if (targetCiIndex !== -1) {
        clonedCis.splice(targetCiIndex, 1);
        setCis(clonedCis);
      }
    } else {
      warningToast(cidDeletedError || t('errors_standard'));
    }

    setOpen(false);
  };

  const openModalCallback = (ciId: string) => {
    setOpen(true);
    setRowId(ciId);
  };

  const { customerInitiativeListColumnConfig, ciTitleFieldConfig, ciTemplateSortFieldConfig } = useCiTemplateListConfig(
    editCiTemplateCallback,
    duplicateCiTemplateCallback,
    addChildCallback,
    openModalCallback,
  );

  const newCiTemplateClick = () => {
    navigate(mayaCiTemplateCreateRoute);
  };

  const newElementButton = {
    newElementLabel: t('maya_list_new_element_label', { elementTitle: 'Template' }),
    newElementClick: newCiTemplateClick,
  };

  const handleSortChange = useCallback((value: SelectOption) => {
    setCiTemplateSortFilter(value.id as SortFilter);
  }, []);

  return {
    customerInitiativeListColumnConfig,
    ciTitleFieldConfig,
    handleChangeCiTitle,
    newElementButton,
    cis,
    open,
    setOpen,
    deleteCiCallback,
    rowId,
    loading,
    onPageChange,
    onPageSizeChange,
    rowCountState,
    removeCiLoading,
    ciTemplateSortFieldConfig,
    handleSortChange,
    ciTemplateSortFilter,
  };
};

export default useCiTemplateList;
